import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, AlertTriangle, FileCheck, Beaker } from 'lucide-react';

const CompliancePage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-purple-700 text-white py-16 pt-32">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-purple-200 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-10 h-10 text-amber-400" />
            <h1 className="text-4xl font-bold">Compliance</h1>
          </div>
          <p className="text-purple-200 mt-2">Regulatory & Quality Compliance Information</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          
          {/* Important Notice */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-amber-800 text-lg">Research Use Only</h3>
                <p className="text-amber-700 mt-2">
                  All AminoChain products are sold exclusively for laboratory research and educational purposes. 
                  They are <strong>NOT</strong> intended for human or animal consumption, therapeutic use, or diagnostic procedures.
                </p>
              </div>
            </div>
          </div>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment to Compliance</h2>
            <p className="text-gray-600 leading-relaxed">
              AminoChain is committed to operating in full compliance with all applicable federal, state, and local laws and regulations governing the sale and distribution of research peptides and compounds. We maintain rigorous quality control standards and ethical business practices to ensure the integrity of our products and services.
            </p>
          </section>

          <section>
            <div className="flex items-center gap-3 mb-4">
              <Beaker className="w-8 h-8 text-purple-600" />
              <h2 className="text-2xl font-bold text-gray-900">Quality Assurance</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-4">
              Every product sold by AminoChain undergoes rigorous quality control testing to ensure:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>Purity Verification:</strong> All peptides are tested to verify minimum 99% purity using HPLC analysis</li>
              <li><strong>Identity Confirmation:</strong> Mass spectrometry confirms molecular weight and identity</li>
              <li><strong>Certificate of Analysis:</strong> Every batch includes a detailed COA documenting test results</li>
              <li><strong>Proper Storage:</strong> Products are stored under appropriate conditions to maintain stability</li>
              <li><strong>Secure Packaging:</strong> All products are packaged to protect integrity during shipping</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-3 mb-4">
              <FileCheck className="w-8 h-8 text-purple-600" />
              <h2 className="text-2xl font-bold text-gray-900">Regulatory Compliance</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-4">
              AminoChain adheres to the following regulatory frameworks and standards:
            </p>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">FDA Regulations</h3>
            <p className="text-gray-600 leading-relaxed">
              Our products are not FDA-approved drugs or supplements. They are chemical compounds sold strictly for research purposes. We clearly label all products as "For Research Use Only" and "Not for Human Consumption" in compliance with FDA guidelines.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">DEA Compliance</h3>
            <p className="text-gray-600 leading-relaxed">
              AminoChain does not sell any controlled substances. All products are legal research compounds that do not require DEA registration for purchase or possession when used for legitimate research purposes.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">State Regulations</h3>
            <p className="text-gray-600 leading-relaxed">
              We monitor and comply with state-specific regulations regarding the sale of research compounds. Certain products may have shipping restrictions to specific states based on local laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Customer Responsibilities</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              By purchasing from AminoChain, customers agree to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Use products only for legitimate research purposes</li>
              <li>Not use products for human or animal consumption</li>
              <li>Comply with all applicable laws and regulations in their jurisdiction</li>
              <li>Maintain appropriate laboratory facilities for handling research compounds</li>
              <li>Properly store and dispose of products according to safety guidelines</li>
              <li>Not resell products without proper licensing and authorization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Prohibited Activities</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              The following activities are strictly prohibited and may result in order cancellation, account termination, and legal action:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Purchasing products for human or animal consumption</li>
              <li>Reselling products without proper authorization</li>
              <li>Misrepresenting the intended use of products</li>
              <li>Providing false information during the ordering process</li>
              <li>Using products for any illegal purpose</li>
              <li>Exporting products in violation of export control laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Documentation & Records</h2>
            <p className="text-gray-600 leading-relaxed">
              AminoChain maintains comprehensive records of all transactions, including customer information, order details, and shipping records. These records are kept in accordance with applicable regulations and may be made available to regulatory authorities upon lawful request.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Reporting Concerns</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have concerns about compliance issues or wish to report suspected misuse of our products, please contact us immediately:
            </p>
            <div className="mt-4 p-4 bg-purple-50 rounded-lg">
              <p className="font-semibold text-purple-900">AminoChain Compliance</p>
              <p className="text-purple-700">Email: support@amino-chain.com</p>
            </div>
            <p className="text-gray-600 leading-relaxed mt-4">
              All reports will be investigated thoroughly, and appropriate action will be taken.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default CompliancePage;
